import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { CompanyService } from "services/companyService";
import { GeneralJournalService } from "services/general-journal";
import { formatDate, formatVndIntegerValue } from "utils";
import toast from "react-hot-toast";

const defaultYear = String(new Date().getFullYear());

const ui = {
  title: { fontSize: 20, lineHeight: 1.2 },
  table: { fontSize: 13 },
  th: { border: "1px solid #000", padding: 6, textAlign: "center", fontSize: 13 },
  tdLeft: { border: "1px solid #000", padding: 6, textAlign: "left", fontSize: 13 },
  tdCenter: { border: "1px solid #000", padding: 6, textAlign: "center", fontSize: 13 },
  tdRight: { border: "1px solid #000", padding: 6, textAlign: "right", fontSize: 13 },
};

const formatMoney = (value) => formatVndIntegerValue(value || 0);

const buildPrintTemplate = ({ unit, companyAddress, filters, trackedAccount, detailColumns, items, summary }) => {
  const fixedColumns = Array.from({ length: 4 }).map((_, index) => detailColumns[index] || null);
  const bodyRows = items
    .map(
      (item) => `
      <tr>
        <td class="cell center">${formatDate(item.recordDate)}</td>
        <td class="cell center">${item.voucherNumber || ""}</td>
        <td class="cell center">${formatDate(item.voucherDate)}</td>
        <td class="cell left">${item.description || ""}</td>
        <td class="cell right">${item.debitAmount ? formatMoney(item.debitAmount) : ""}</td>
        ${fixedColumns
          .map(
            (column) =>
              `<td class="cell right">${column && Number(item?.detailAmounts?.[column.key] || 0) ? formatMoney(item.detailAmounts[column.key]) : ""}</td>`
          )
          .join("")}
        <td class="cell right">${item.otherAccountAmount ? formatMoney(item.otherAccountAmount) : ""}</td>
        <td class="cell center">${(item.otherAccountCodes || []).join(", ")}</td>
      </tr>`
    )
    .join("");

  return `
  <html>
    <head>
      <meta charset="UTF-8" />
      <title>Sổ Nhật Ký Thu Tiền S03a1-DN</title>
      <style>
        body { font-family: 'Times New Roman', serif; color: #000; margin: 24px; font-size: 14px; }
        .header { display: flex; justify-content: space-between; margin-bottom: 12px; }
        .cell, .head { border: 1px solid #000; padding: 6px; vertical-align: middle; }
        .head { font-weight: 700; text-align: center; }
        table { width: 100%; border-collapse: collapse; }
        .title { text-align: center; margin: 10px 0 16px; }
        .title h1 { margin: 0; font-size: 26px; text-transform: uppercase; }
        .title p { margin: 4px 0; }
        .center { text-align: center; }
        .left { text-align: left; }
        .right { text-align: right; }
        .sign { margin-top: 48px; display: flex; justify-content: space-between; align-items: flex-start; text-align: center; }
        .sign-col { width: 32%; }
        .sign-date-row { min-height: 20px; margin-bottom: 2px; }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          <div><strong>Đơn vị:</strong> ${unit || "......................................"}</div>
          <div><strong>Địa chỉ:</strong> ${companyAddress || "......................................"}</div>
        </div>
        <div class="center">
          <div><strong>Mẫu số S03a1-DN</strong></div>
          <div><em>(Kèm theo Thông tư số 99/2025/TT-BTC ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</em></div>
        </div>
      </div>
      <div class="title">
        <h1>Sổ Nhật Ký Thu Tiền</h1>
        <p>Năm: ${filters.year}</p>
      </div>
      <table>
        <thead>
          <tr>
            <th class="head" rowspan="2">Ngày, tháng ghi sổ<br/>A</th>
            <th class="head" colspan="2">Chứng từ</th>
            <th class="head" rowspan="2">Diễn giải<br/>D</th>
            <th class="head" rowspan="2">Ghi Nợ TK ${trackedAccount.accountCode || filters.accountCode || "..."}<br/>1</th>
            <th class="head" colspan="6">Ghi Có các TK</th>
          </tr>
          <tr>
            <th class="head">Số hiệu<br/>B</th>
            <th class="head">Ngày tháng<br/>C</th>
            ${fixedColumns
              .map(
                (column, index) =>
                  `<th class="head">${column?.accountCode || "..."}<br/>${index + 2}</th>`
              )
              .join("")}
            <th class="head">Tài khoản khác - Số tiền<br/>6</th>
            <th class="head">Tài khoản khác - Số hiệu<br/>E</th>
          </tr>
        </thead>
        <tbody>
          ${bodyRows || '<tr><td class="cell center" colspan="11">Không có dữ liệu</td></tr>'}
          <tr>
            <td class="cell left" colspan="4"><strong>Cộng số phát sinh</strong></td>
            <td class="cell right"><strong>${formatMoney(summary.totalDebitAmount || 0)}</strong></td>
            ${fixedColumns
              .map(
                (column) =>
                  `<td class="cell right"><strong>${column ? formatMoney(summary?.totalDetailAmounts?.[column.key] || 0) : ""}</strong></td>`
              )
              .join("")}
            <td class="cell right"><strong>${formatMoney(summary.totalOtherAccountAmount || 0)}</strong></td>
            <td class="cell center"></td>
          </tr>
        </tbody>
      </table>
      <div class="sign">
        <div class="sign-col">
          <div class="sign-date-row">&nbsp;</div>
          <div><strong>Người ghi sổ</strong></div>
          <div><em>(Ký, họ tên)</em></div>
        </div>
        <div class="sign-col">
          <div class="sign-date-row">&nbsp;</div>
          <div><strong>Kế toán trưởng</strong></div>
          <div><em>(Ký, họ tên)</em></div>
        </div>
        <div class="sign-col">
          <div class="sign-date-row"><em>Ngày ...... tháng ...... năm ......</em></div>
          <div><strong>Người đại diện theo pháp luật</strong></div>
          <div><em>(Ký, họ tên, đóng dấu)</em></div>
        </div>
      </div>
    </body>
  </html>`;
};

export default function CashReceiptsJournalDetail({ baseRoute = "/templates/cash-receipts-journal" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [companyId, setCompanyId] = useState("");
  const [unit, setUnit] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [filters, setFilters] = useState({
    year: defaultYear,
    fromDate: "",
    toDate: "",
    accountCode: "",
    searchBy: "",
  });
  const [trackedAccount, setTrackedAccount] = useState({ accountCode: "", accountName: "" });
  const [detailColumns, setDetailColumns] = useState([]);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({
    totalRows: 0,
    totalDebitAmount: 0,
    totalDetailAmounts: {},
    totalOtherAccountAmount: 0,
    postedToLedgerCount: 0,
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || "");
    setFilters({
      year: searchParams.get("year") || defaultYear,
      fromDate: searchParams.get("fromDate") || "",
      toDate: searchParams.get("toDate") || "",
      accountCode: searchParams.get("accountCode") || "",
      searchBy: searchParams.get("searchBy") || "",
    });
  }, [location.search]);

  useEffect(() => {
    const init = async () => {
      const response = await CompanyService.getByAuth();
      const payload = response?.data || response || {};
      setCompanyId(payload?._id || "");
      setUnit(payload?.name || payload?.companyName || payload?.unit || "");
      setCompanyAddress(payload?.address || payload?.companyAddress || "");
    };
    init();
  }, []);

  const fixedColumns = useMemo(() => Array.from({ length: 4 }).map((_, index) => detailColumns[index] || null), [detailColumns]);

  const fetchData = useCallback(async () => {
    if (!companyId || !filters.year || !filters.accountCode) return;
    try {
      setLoading(true);
      const response = await GeneralJournalService.getCashReceiptJournal({
        company: companyId,
        year: Number(filters.year),
        accountCode: filters.accountCode,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        searchBy: filters.searchBy || undefined,
        paginate: false,
      });
      const payload = response?.data || response || {};
      setTrackedAccount(payload.trackedAccount || { accountCode: filters.accountCode, accountName: filters.accountCode });
      setDetailColumns(payload.detailColumns || []);
      setItems(payload.items || []);
      setSummary(
        payload.summary || {
          totalRows: 0,
          totalDebitAmount: 0,
          totalDetailAmounts: {},
          totalOtherAccountAmount: 0,
          postedToLedgerCount: 0,
        }
      );
    } catch (error) {
      toast.error(error?.response?.data?.message || "Không thể tải chi tiết Sổ nhật ký thu tiền");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePrint = () => {
    const html = buildPrintTemplate({ unit, companyAddress, filters, trackedAccount, detailColumns, items, summary });
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Trình duyệt đang chặn cửa sổ in. Vui lòng cho phép pop-up.");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 150);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={11}>
            <Card>
              <SoftBox p={3} display="flex" flexDirection="column" minHeight="calc(100vh - 220px)">
                <SoftBox mb={2}>
                  <SoftTypography variant="h5" fontWeight="bold" sx={ui.title}>
                    Sổ Nhật Ký Thu Tiền (S03a1-DN)
                  </SoftTypography>
                </SoftBox>
                <SoftBox border="1px solid #000" p={3} sx={{ fontFamily: "'Times New Roman', serif", color: "#000" }}>
                  <SoftBox display="flex" justifyContent="space-between" mb={2}>
                    <SoftBox>
                      <div style={{ fontSize: 14 }}><strong>Đơn vị:</strong> {unit || "......................................"}</div>
                      <div style={{ fontSize: 14 }}><strong>Địa chỉ:</strong> {companyAddress || "......................................"}</div>
                    </SoftBox>
                    <SoftBox textAlign="center" width="48%">
                      <div style={{ fontSize: 14, fontWeight: 700 }}>Mẫu số S03a1-DN</div>
                      <div style={{ fontSize: 14, fontStyle: "italic" }}>
                        (Kèm theo Thông tư số 99/2025/TT-BTC ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)
                      </div>
                    </SoftBox>
                  </SoftBox>
                  <SoftBox textAlign="center" mb={2}>
                    <div style={{ fontSize: 28, fontWeight: 700, textTransform: "uppercase" }}>Sổ Nhật Ký Thu Tiền</div>
                    <div style={{ fontSize: 14, marginTop: 6 }}>Năm: {filters.year}</div>
                    <div style={{ fontSize: 14, marginTop: 4 }}>
                      Tài khoản tiền: {trackedAccount.accountCode || filters.accountCode || "..."}
                      {trackedAccount.accountName ? ` - ${trackedAccount.accountName}` : ""}
                    </div>
                  </SoftBox>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1280, ...ui.table }}>
                      <thead>
                        <tr>
                          <th style={ui.th} rowSpan={2}>Ngày, tháng ghi sổ<br />A</th>
                          <th style={ui.th} colSpan={2}>Chứng từ</th>
                          <th style={ui.th} rowSpan={2}>Diễn giải<br />D</th>
                          <th style={ui.th} rowSpan={2}>Ghi Nợ TK {trackedAccount.accountCode || filters.accountCode || "..."}<br />1</th>
                          <th style={ui.th} colSpan={6}>Ghi Có các TK</th>
                        </tr>
                        <tr>
                          <th style={ui.th}>Số hiệu<br />B</th>
                          <th style={ui.th}>Ngày tháng<br />C</th>
                          {fixedColumns.map((column, index) => (
                            <th key={column?.accountCode || `fixed-${index}`} style={ui.th}>
                              {column?.accountCode || "..."}
                              <br />
                              {index + 2}
                            </th>
                          ))}
                          <th style={ui.th}>Tài khoản khác - Số tiền<br />6</th>
                          <th style={ui.th}>Tài khoản khác - Số hiệu<br />E</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading && (
                          <tr>
                            <td style={ui.tdCenter} colSpan={11}>Đang tải dữ liệu...</td>
                          </tr>
                        )}
                        {!loading && !items.length && (
                          <tr>
                            <td style={ui.tdCenter} colSpan={11}>Không có dữ liệu</td>
                          </tr>
                        )}
                        {!loading && items.map((item, index) => (
                          <tr key={`${item.journalId || 'row'}-${index}`}>
                            <td style={ui.tdCenter}>{formatDate(item.recordDate)}</td>
                            <td style={ui.tdCenter}>{item.voucherNumber || ""}</td>
                            <td style={ui.tdCenter}>{formatDate(item.voucherDate)}</td>
                            <td style={ui.tdLeft}>{item.description || ""}</td>
                            <td style={ui.tdRight}>{item.debitAmount ? formatMoney(item.debitAmount) : ""}</td>
                            {fixedColumns.map((column, columnIndex) => (
                              <td key={`${item.journalId || index}-${column?.accountCode || columnIndex}`} style={ui.tdRight}>
                                {column && Number(item?.detailAmounts?.[column.key] || 0)
                                  ? formatMoney(item.detailAmounts[column.key])
                                  : ""}
                              </td>
                            ))}
                            <td style={ui.tdRight}>{item.otherAccountAmount ? formatMoney(item.otherAccountAmount) : ""}</td>
                            <td style={ui.tdCenter}>{(item.otherAccountCodes || []).join(", ")}</td>
                          </tr>
                        ))}
                        <tr>
                          <td style={{ ...ui.tdLeft, fontWeight: 700 }} colSpan={4}>Cộng số phát sinh</td>
                          <td style={{ ...ui.tdRight, fontWeight: 700 }}>{formatMoney(summary.totalDebitAmount || 0)}</td>
                          {fixedColumns.map((column, index) => (
                            <td key={column?.accountCode || `summary-${index}`} style={{ ...ui.tdRight, fontWeight: 700 }}>
                              {column ? formatMoney(summary?.totalDetailAmounts?.[column.key] || 0) : ""}
                            </td>
                          ))}
                          <td style={{ ...ui.tdRight, fontWeight: 700 }}>{formatMoney(summary.totalOtherAccountAmount || 0)}</td>
                          <td style={ui.tdCenter}></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <SoftBox mt={2} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                    <SoftTypography variant="caption" color="text">
                      Tổng số dòng: {summary.totalRows} - Đã đánh dấu Sổ Cái: {summary.postedToLedgerCount}
                    </SoftTypography>
                    <SoftTypography variant="caption" color="text">
                      Tổng thu: {formatMoney(summary.totalDebitAmount || 0)}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
                <SoftBox mt="auto" pt={2} display="flex" justifyContent="flex-end" gap={1}>
                  <SoftButton variant="outlined" color="dark" onClick={() => navigate(baseRoute)}>
                    Quay lại
                  </SoftButton>
                  <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                    In Bảng
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
